import { cssVar } from '@savgroup-front-common/primitive/helpers/cssVar';
import { spacing } from '@savgroup-front-common/primitive/helpers/spacing';
import { rem } from '@savgroup-front-common/primitive/helpers/units';

/**
 * Single source of truth for everything Tailwind-related in the theme.
 *
 * It gathers the Tailwind-backed scales (`spacing`, font `size`, font `weight`,
 * `line-height`, `border-radius`) exposed as the opt-in `theme.tw` namespace, so
 * all the tokens that mirror Tailwind's CSS variables live in one place instead
 * of being spread across several files.
 */

/* -------------------------------------------------------------------------- */
/* Spacing scale — `theme.tw.spacing[n]`                                         */
/* -------------------------------------------------------------------------- */

/**
 * Tailwind spacing scale, exposed as `theme.tw.spacing[n]`.
 *
 * Each step resolves to `calc(var(--spacing) * n)` — exactly how Tailwind
 * compiles its `p-2` / `gap-4` utilities — so styled-components share the same
 * runtime token as the Tailwind classes. A rem fallback on `--spacing` keeps the
 * scale working in apps that don't load the Tailwind stylesheet. Half-steps
 * (`0.5`, `1.5`…) and the single-pixel `px` step mirror Tailwind's own scale.
 */
/**
 * Named spacing aliases, exposed alongside the numeric scale as
 * `theme.tw.spacing.<token>`. Each alias maps to a step of the numeric Tailwind
 * spacing scale, mirroring the documented component spacing scale
 * (`docs/Design System/06-spacing.md`), so `theme.tw.spacing.md` and
 * `theme.tw.spacing[3]` resolve to the same runtime `--spacing` multiple.
 */
export interface TailwindSpacingAliasScale {
  xxs: string;
  xs: string;
  sm: string;
  md: string;
  lg: string;
  xl: string;
  xxl: string;
}

const tailwindSpacingAliasScale: TailwindSpacingAliasScale = {
  xxs: spacing('xxs'),
  xs: spacing('xs'),
  sm: spacing('sm'),
  md: spacing('md'),
  lg: spacing('lg'),
  xl: spacing('xl'),
  xxl: spacing('xxl'),
};

const tailwindSpacingScale = {
  ...tailwindSpacingAliasScale,
  0: spacing(0),
  0.5: spacing(0.5),
  1: spacing(1),
  1.5: spacing(1.5),
  2: spacing(2),
  2.5: spacing(2.5),
  3: spacing(3),
  3.5: spacing(3.5),
  4: spacing(4),
  5: spacing(5),
  6: spacing(6),
  7: spacing(7),
  8: spacing(8),
  9: spacing(9),
  10: spacing(10),
  11: spacing(11),
  12: spacing(12),
  14: spacing(14),
  16: spacing(16),
  20: spacing(20),
  24: spacing(24),
  28: spacing(28),
  32: spacing(32),
  36: spacing(36),
  40: spacing(40),
  44: spacing(44),
  48: spacing(48),
  52: spacing(52),
  56: spacing(56),
  60: spacing(60),
  64: spacing(64),
  72: spacing(72),
  80: spacing(80),
  96: spacing(96),
  px: spacing('px'),
};

export type TailwindSpacingScale = typeof tailwindSpacingScale;

/* -------------------------------------------------------------------------- */
/* Font size scale — `theme.fonts.size.<token>`                               */
/* -------------------------------------------------------------------------- */

/**
 * Tailwind text (font-size) scale, exposed as `theme.fonts.size.<token>`.
 *
 * Each token points directly at Tailwind's `--text-*` CSS variable, with a rem
 * fallback so the token still resolves in apps that don't load the Tailwind
 * stylesheet. Keys that start with a digit (`2xl`…`9xl`) require bracket access:
 * `theme.fonts.size['2xl']`.
 */
export interface TailwindFontSizeScale {
  xxs: string;
  xs: string;
  sm: string;
  base: string;
  lg: string;
  xl: string;
  '2xl': string;
  '3xl': string;
  '4xl': string;
  '5xl': string;
  '6xl': string;
  '7xl': string;
  '8xl': string;
  '9xl': string;
}

const tailwindFontSizeScale: TailwindFontSizeScale = {
  xxs: cssVar('--text-xxs', rem(10)),
  xs: cssVar('--text-xs', rem(12)),
  sm: cssVar('--text-sm', rem(14)),
  base: cssVar('--text-base', rem(16)),
  lg: cssVar('--text-lg', rem(18)),
  xl: cssVar('--text-xl', rem(20)),
  '2xl': cssVar('--text-2xl', rem(24)),
  '3xl': cssVar('--text-3xl', rem(30)),
  '4xl': cssVar('--text-4xl', rem(36)),
  '5xl': cssVar('--text-5xl', rem(48)),
  '6xl': cssVar('--text-6xl', rem(60)),
  '7xl': cssVar('--text-7xl', rem(72)),
  '8xl': cssVar('--text-8xl', rem(96)),
  '9xl': cssVar('--text-9xl', rem(128)),
};

/* -------------------------------------------------------------------------- */
/* Font weight scale — `theme.fonts.weight.<token>`                           */
/* -------------------------------------------------------------------------- */

export interface TailwindFontWeightScale {
  thin: string;
  extralight: string;
  light: string;
  normal: string;
  medium: string;
  semibold: string;
  bold: string;
  extrabold: string;
  black: string;
}

const tailwindFontWeightScale: TailwindFontWeightScale = {
  thin: cssVar('--font-weight-thin', '100'),
  extralight: cssVar('--font-weight-extralight', '200'),
  light: cssVar('--font-weight-light', '300'),
  normal: cssVar('--font-weight-normal', '400'),
  medium: cssVar('--font-weight-medium', '500'),
  semibold: cssVar('--font-weight-semibold', '600'),
  bold: cssVar('--font-weight-bold', '700'),
  extrabold: cssVar('--font-weight-extrabold', '800'),
  black: cssVar('--font-weight-black', '900'),
};

/* -------------------------------------------------------------------------- */
/* Line-height scale                                                          */
/* -------------------------------------------------------------------------- */

export interface TailwindLineHeightScale {
  // Named Tailwind leading tokens.
  tight: string;
  snug: string;
  normal: string;
  relaxed: string;
  loose: string;
  // Per-text-size paired line-heights (`--text-<token>--line-height`),
  // pairing each `theme.tw.font.size.<token>` with its matching line-height.
  xxs: string;
  xs: string;
  sm: string;
  base: string;
  lg: string;
  xl: string;
  '2xl': string;
  '3xl': string;
  '4xl': string;
  '5xl': string;
  '6xl': string;
  '7xl': string;
  '8xl': string;
  '9xl': string;
}

const tailwindLineHeightScale: TailwindLineHeightScale = {
  tight: cssVar('--leading-tight', '1.25'),
  snug: cssVar('--leading-snug', '1.375'),
  normal: cssVar('--leading-normal', '1.5'),
  relaxed: cssVar('--leading-relaxed', '1.625'),
  loose: cssVar('--leading-loose', '2'),
  xxs: cssVar('--text-xxs--line-height', 'calc(0.875 / 0.625)'),
  xs: cssVar('--text-xs--line-height', 'calc(1 / 0.75)'),
  sm: cssVar('--text-sm--line-height', 'calc(1.25 / 0.875)'),
  base: cssVar('--text-base--line-height', '1.5'),
  lg: cssVar('--text-lg--line-height', 'calc(1.75 / 1.125)'),
  xl: cssVar('--text-xl--line-height', 'calc(1.75 / 1.25)'),
  '2xl': cssVar('--text-2xl--line-height', 'calc(2 / 1.5)'),
  '3xl': cssVar('--text-3xl--line-height', 'calc(2.25 / 1.875)'),
  '4xl': cssVar('--text-4xl--line-height', 'calc(2.5 / 2.25)'),
  '5xl': cssVar('--text-5xl--line-height', '1'),
  '6xl': cssVar('--text-6xl--line-height', '1'),
  '7xl': cssVar('--text-7xl--line-height', '1'),
  '8xl': cssVar('--text-8xl--line-height', '1'),
  '9xl': cssVar('--text-9xl--line-height', '1'),
};

/* -------------------------------------------------------------------------- */
/* Border-radius scale — `theme.borders.radius.<token>`                       */
/* -------------------------------------------------------------------------- */

/**
 * Tailwind border-radius scale, exposed as `theme.borders.radius.<token>`.
 *
 * Each token points at Tailwind's `--radius-*` CSS variable, with a rem/px
 * fallback so it still resolves in apps that don't load the Tailwind stylesheet.
 * `none` and `full` mirror Tailwind's `rounded-none` / `rounded-full` utilities.
 */
export interface TailwindBorderRadiusScale {
  none: string;
  xs: string;
  sm: string;
  md: string;
  lg: string;
  xl: string;
  '2xl': string;
  '3xl': string;
  '4xl': string;
  full: string;
}

const tailwindBorderRadiusScale: TailwindBorderRadiusScale = {
  none: '0',
  xs: cssVar('--radius-xs', rem(2)),
  sm: cssVar('--radius-sm', rem(4)),
  md: cssVar('--radius-md', rem(6)),
  lg: cssVar('--radius-lg', rem(8)),
  xl: cssVar('--radius-xl', rem(12)),
  '2xl': cssVar('--radius-2xl', rem(16)),
  '3xl': cssVar('--radius-3xl', rem(24)),
  '4xl': cssVar('--radius-4xl', rem(32)),
  full: '9999px',
};

/* -------------------------------------------------------------------------- */
/* Grouped scales — single entry point for the Tailwind-backed theme tokens   */
/* -------------------------------------------------------------------------- */

/**
 * All the Tailwind-backed scales grouped under one object, exposed on the theme
 * as the dedicated `theme.tw` namespace so they live alongside — and never
 * override — the legacy base theme tokens. Consumers opt into Tailwind tokens
 * explicitly: `theme.tw.spacing[4]`, `theme.tw.radius.md`,
 * `theme.tw.font.size.base`, `theme.tw.font.weight.semibold`,
 * `theme.tw.font.lineHeight.base`.
 */
export const tailwindTheme = {
  spacing: tailwindSpacingScale,
  radius: tailwindBorderRadiusScale,
  font: {
    size: tailwindFontSizeScale,
    weight: tailwindFontWeightScale,
    lineHeight: tailwindLineHeightScale,
  },
};

export type TailwindTheme = typeof tailwindTheme;
